
import * as React from 'react';
import { StatePropsUpdater } from '../../utilities/components/RenderProps';
import { IActionStateProps, } from '../withAction/WithAction';
import { IComponentDecorator, IDecoratedComponentType, IDecoratedProps } from '../../utilities/components/Decorators';
import { createRelay, IRelayReceiverStateProps } from '../../utilities/components/Relay';
import { mapObject } from '../../utilities/objects';
import { MirrorPatch, Delete } from '../../utilities/Graft';
import { ISelectionZoneProps, IObjectWithKey } from 'office-ui-fabric-react/lib/Selection';
import { AutoKeyFacet } from '../../models/AutoKeyFacet';

const actionInvokerIdKeyFacet = new AutoKeyFacet('actionInvokerId');

export interface IRegisterInvokeActionProps extends IActionStateProps {
    itemKey: string | undefined;
    index?: number;
}

interface IActionInvokerActionProps extends IRegisterInvokeActionProps {
    // Nothing.
}

interface IActionInvokerRelayState {
    actions: {
        [id: string]: IActionInvokerActionProps;
    };
}

const INITIAL_ACTION_INVOKER_STATE: IActionInvokerRelayState = {
    actions: {}
};

const ACTION_INVOKER_RELAY = createRelay<IActionInvokerRelayState>(INITIAL_ACTION_INVOKER_STATE, {
    merge: (current: IActionInvokerRelayState) => {
        return {
            actions: mapObject(current.actions, (child: IActionInvokerActionProps) => {
                return new MirrorPatch(child);
            })
        };
    },
    remove: (current: IActionInvokerRelayState) => {
        return {
            actions: mapObject(current.actions, () => {
                return new Delete<IActionInvokerActionProps>();
            })
        };
    },
    allowSenderOutsideReceiver: true
});

export type IWithActionInvokerInjectedProps = Pick<ISelectionZoneProps, 'onItemInvoked'>;

/**
 * Creates a context for selection-based invocation of actions.
 * Since selection components such as `SelectionZone` centeralize the dispatch for which item has been 'invoked'
 * by the user, this component collects registration of `Action` components as the 'invoke' actions for the
 * selectable items, wiring up the `onItemInvoked` callback to execute the correct action instance for
 * a given item.
 */
export function withActionInvoker(): IComponentDecorator<IWithActionInvokerInjectedProps, {}> {
    return <P extends IWithActionInvokerInjectedProps>(
        component: React.ComponentType<P>
    ): IDecoratedComponentType<P, IWithActionInvokerInjectedProps, {}> => {
        const Component = component;

        type IProps = IDecoratedProps<P, IWithActionInvokerInjectedProps, {}>;

        class ActionInvokerHost extends React.PureComponent<IProps> {
            private _state: IActionInvokerRelayState | undefined;

            public render(): JSX.Element {
                return (
                    <ACTION_INVOKER_RELAY.Receiver
                        values={ {
                            '': (
                                <Component
                                    { ...this.props as P }
                                    onItemInvoked={ this._onItemInvoked }
                                />
                            )
                        } }
                    >
                        { (relayState: IRelayReceiverStateProps<IActionInvokerRelayState>) => {
                            const state = relayState.values['']!; // tslint:disable-line:no-non-null-assertion

                            return (
                                <StatePropsUpdater stateProps={ state } onUpdate={ this._onUpdateActionInvokerState } />
                            );
                        } }
                    </ACTION_INVOKER_RELAY.Receiver>
                );
            }

            private _onUpdateActionInvokerState = (state: IActionInvokerRelayState): void => {
                this._state = state;
            }

            private _onItemInvoked = (
                item?: IObjectWithKey | undefined,
                index?: number | undefined,
                ev?: Event | undefined
            ): void => {
                const itemKey = item && item.key;

                const state = this._state;

                if (state) {
                    for (const id in state.actions) {
                        if (Object.prototype.hasOwnProperty.call(state.actions, id)) {
                            const action = state.actions[id];

                            if (action.itemKey === itemKey &&
                                (typeof action.index === 'undefined' || action.index === index)) {
                                if (action.isAvailable) {
                                    action.execute({
                                        event: ev as {} as MouseEvent
                                    }).catch(() => {
                                        // Action is already logged.
                                    });

                                    break;
                                }
                            }
                        }
                    }
                }
            }
        }

        return ActionInvokerHost;
    };
}

/**
 * Registers a source of an 'invoke' action for the specific `itemKey` when used within a component
 * which has used `withActionInvoker` to decorate an ancestor within its hierarchy.
 * This ensures that using a component such as `SelectionZone` to 'invoke' an item will execute
 * the action whose state is provided to `RegisterInvokeAction`.
 */
export class RegisterInvokeAction extends React.PureComponent<IRegisterInvokeActionProps> {
    private _id: string;

    constructor(props: IRegisterInvokeActionProps) {
        super(props);

        this._id = actionInvokerIdKeyFacet.serializeNext();
    }

    public render(): JSX.Element | null {
        if (!this.props.itemKey) {
            return null;
        }

        return (
            <ACTION_INVOKER_RELAY.Sender
                actions={ {
                    [this._id]: this.props
                } }
            />
        );
    }
}
